import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Email as EmailIcon,
  Edit as EditIcon,
  FileDownload as FileDownloadIcon,
  Groups as GroupsIcon,
  PersonAddAlt1 as PersonAddIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Stars as StarsIcon
} from '@mui/icons-material';
import { API_ENDPOINTS, BRAND_CONFIG, getCurrentBrand } from '../config/api';

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-MY', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur'
  }).format(date);
};

const StatCard = ({ icon, label, value, hint, colors }) => (
  <Paper sx={{ p: 2.25, borderRadius: 3, minHeight: 122, border: '1px solid #edf0ff', boxShadow: '0 9px 24px rgba(51, 72, 129, 0.08)' }}>
    <Box display="flex" justifyContent="space-between" gap={1.5}>
      <Box>
        <Typography sx={{ color: '#70809f', fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 850, color: '#19264a' }}>{value}</Typography>
        <Typography variant="body2" sx={{ mt: 0.4, color: '#7b89a3' }}>{hint}</Typography>
      </Box>
      <Avatar sx={{ width: 50, height: 50, background: colors, boxShadow: '0 8px 16px rgba(54, 104, 225, 0.16)' }}>{icon}</Avatar>
    </Box>
  </Paper>
);

const Members = () => {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBERS}?brand=${encodeURIComponent(getCurrentBrand())}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to load members.');
      setMembers(data.members || data.rows || []);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load members.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return members;
    return members.filter((member) => [member.google_profile_name, member.system_name, member.email, member.phone_number]
      .some((value) => String(value || '').toLowerCase().includes(query)));
  }, [members, search]);

  const openEdit = (member) => {
    setSaveError('');
    setEditingMember({ ...member, system_name: member.system_name || '', email: member.email || '', phone_number: member.phone_number || '', points: member.points || 0 });
  };

  const saveMember = async () => {
    if (!editingMember) return;
    setSaving(true);
    setSaveError('');
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBERS}/${editingMember.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify({
          system_name: editingMember.system_name,
          email: editingMember.email,
          phone_number: editingMember.phone_number,
          points: Number(editingMember.points)
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to save member.');
      setMembers((current) => current.map((member) => member.id === data.member.id ? { ...member, ...data.member } : member));
      setEditingMember(null);
    } catch (saveMemberError) {
      setSaveError(saveMemberError.message || 'Unable to save member.');
    } finally {
      setSaving(false);
    }
  };

  const exportMembers = () => {
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Google profile name', 'System name', 'Email', 'Phone number', 'Points', 'Joined'],
      ...filteredMembers.map((member) => [member.google_profile_name || '', member.system_name || '', member.email || '', member.phone_number || '', member.points || 0, formatDate(member.created_at)])
    ];
    const file = new Blob([rows.map((row) => row.map(escapeCsv).join(',')).join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${getCurrentBrand()}-members-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const analytics = useMemo(() => {
    const today = new Date();
    const thisMonth = members.filter((member) => {
      if (!member.created_at) return false;
      const date = new Date(member.created_at);
      return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    }).length;

    return {
      thisMonth,
      withPhone: members.filter((member) => member.phone_number).length,
      totalPoints: members.reduce((total, member) => total + Number(member.points || 0), 0)
    };
  }, [members]);

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto', pb: 4 }}>
      <Paper elevation={8} sx={{ borderRadius: 4, overflow: 'hidden', background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }}>
        <Paper elevation={0} sx={{ background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)', color: 'white', borderRadius: 0, p: { xs: 3, md: 5 }, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', inset: 0, opacity: 0.08, backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px), radial-gradient(circle at 40% 40%, white 1px, transparent 1px)', backgroundSize: '60px 60px, 80px 80px, 40px 40px', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', width: 150, height: 150, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.12)', right: { xs: -65, md: 120 }, top: -45 }} />
          <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2} flexDirection={{ xs: 'column', sm: 'row' }} position="relative">
            <Box display="flex" alignItems="center" gap={{ xs: 2, md: 3 }}>
              <Avatar sx={{ width: { xs: 58, md: 72 }, height: { xs: 58, md: 72 }, bgcolor: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.5)' }}><PeopleIcon sx={{ fontSize: { xs: 30, md: 38 } }} /></Avatar>
              <Box>
                <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' }, lineHeight: 1.1, textShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>Members</Typography>
                <Typography variant="h5" sx={{ opacity: 0.95, mt: 1, fontWeight: 400, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Manage your {BRAND_CONFIG.displayName} community members</Typography>
              </Box>
            </Box>
            <Chip label={`${members.length} total members`} sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 700, border: '1px solid rgba(255,255,255,0.2)' }} />
          </Box>
        </Paper>

        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2.25, mb: 2.5 }}>
        <StatCard icon={<GroupsIcon />} label="Total members" value={members.length.toLocaleString()} hint="All registered members" colors="linear-gradient(135deg, #8569ff, #5648e9)" />
        <StatCard icon={<PersonAddIcon />} label="Joined this month" value={analytics.thisMonth.toLocaleString()} hint="New community sign-ups" colors="linear-gradient(135deg, #29c9ab, #3ba4e8)" />
        <StatCard icon={<PhoneIcon />} label="Profiles complete" value={`${analytics.withPhone}/${members.length}`} hint="Members with a phone number" colors="linear-gradient(135deg, #f5a623, #f08045)" />
        <StatCard icon={<StarsIcon />} label="Member points" value={analytics.totalPoints.toLocaleString()} hint="Points held by all members" colors="linear-gradient(135deg, #ee5e8c, #f26f4e)" />
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
        <Box display="flex" justifyContent="space-between" gap={2} mb={2.5} flexDirection={{ xs: 'column', sm: 'row' }}>
          <TextField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, email, or phone number"
            size="small"
            sx={{ minWidth: { sm: 330 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
          />
          <Box display="flex" gap={1}>
            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportMembers} disabled={loading || filteredMembers.length === 0}>Export CSV</Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadMembers} disabled={loading}>Refresh</Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 930 }} aria-label="registered members">
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#f6f8fa', fontWeight: 800, color: '#52616b' } }}>
                  <TableCell>Google profile name</TableCell>
                  <TableCell>System name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone number</TableCell>
                  <TableCell align="center">Points</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMembers.map((member) => (
                  <TableRow key={member.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ bgcolor: '#e8f3ff', color: '#2980b9', fontWeight: 800 }}>{String(member.google_profile_name || member.email || '?').charAt(0).toUpperCase()}</Avatar>
                        <Typography fontWeight={700}>{member.google_profile_name || 'No Google profile name'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{member.system_name || '—'}</TableCell>
                    <TableCell><Box display="flex" alignItems="center" gap={1}><EmailIcon fontSize="small" color="action" />{member.email || '—'}</Box></TableCell>
                    <TableCell>{member.phone_number ? <Box display="flex" alignItems="center" gap={1}><PhoneIcon fontSize="small" color="action" />{member.phone_number}</Box> : '—'}</TableCell>
                    <TableCell align="center">
                      <Box component="span" sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.45, px: 1, py: 0.42, borderRadius: 999,
                        fontSize: '0.78rem', fontWeight: 800,
                        color: Number(member.points || 0) > 0 ? '#5a37b8' : '#7d8798',
                        bgcolor: Number(member.points || 0) > 0 ? '#f0eaff' : '#f2f4f7',
                        border: Number(member.points || 0) > 0 ? '1px solid #ded1ff' : '1px solid #e2e6ec'
                      }}>
                        {Number(member.points || 0) > 0 && <StarsIcon sx={{ fontSize: 14, color: '#8a56f5' }} />}
                        {Number(member.points || 0).toLocaleString()} pts
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(member.created_at)}</TableCell>
                    <TableCell align="right"><Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(member)}>Edit</Button></TableCell>
                  </TableRow>
                ))}
                {filteredMembers.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 7, color: 'text.secondary' }}>{search ? 'No members match your search.' : 'No members have signed up yet.'}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

        </Box>
      </Paper>

      <Dialog open={Boolean(editingMember)} onClose={() => !saving && setEditingMember(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit member</DialogTitle>
        <DialogContent dividers>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
            <TextField label="Google profile name" value={editingMember?.google_profile_name || '—'} helperText="Read-only. Captured from the member's Google profile at sign-in." InputProps={{ readOnly: true }} fullWidth />
            <TextField label="System name" value={editingMember?.system_name || ''} onChange={(event) => setEditingMember((member) => ({ ...member, system_name: event.target.value }))} fullWidth />
            <TextField label="Email" type="email" value={editingMember?.email || ''} onChange={(event) => setEditingMember((member) => ({ ...member, email: event.target.value }))} required fullWidth />
            <TextField label="Phone number" value={editingMember?.phone_number || ''} onChange={(event) => setEditingMember((member) => ({ ...member, phone_number: event.target.value }))} fullWidth />
            <TextField label="Points" type="number" value={editingMember?.points ?? 0} onChange={(event) => setEditingMember((member) => ({ ...member, points: event.target.value }))} inputProps={{ min: 0, step: 1 }} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditingMember(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveMember} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Members;
